import { supabase } from '@/integrations/supabase/client';

export type RankingType = 'missions' | 'playtime' | 'tested' | 'experience';

export interface RankedPlayer {
    id: string;
    display_name: string;
    avatar_url: string | null;
    value: number;
    formattedValue: string;
    borderStyle?: string;
}

export async function getPlayerRankings(type: RankingType, page: number = 1, limit: number = 50): Promise<{ players: RankedPlayer[], total: number }> {
    try {
        // Fetch players first
        const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url, active_border_id, xp, shop_items!profiles_active_border_id_fkey(metadata)')
            .eq('user_type', 'player');

        if (profilesError || !profiles) {
            console.error('Error fetching profiles for rankings:', profilesError);
            return { players: [], total: 0 };
        }

        let rankedPlayers: RankedPlayer[] = [];

        if (type === 'experience') {
            rankedPlayers = profiles.map(p => {
                const shopItem: any = p.shop_items;
                const borderStyle = shopItem?.metadata?.style || shopItem?.metadata?.image_url;

                return {
                    id: p.id,
                    display_name: p.display_name,
                    avatar_url: p.avatar_url,
                    value: p.xp || 0,
                    formattedValue: `${(p.xp || 0).toLocaleString()} XP`,
                    borderStyle: borderStyle
                };
            });
        } else if (type === 'playtime' || type === 'tested') {
            const { data: playtimeData, error: playtimeError } = await supabase
                .from('playtime_tracking')
                .select('player_id, steam_playtime');

            if (playtimeError) throw playtimeError;

            const statsMap = new Map<string, { total: number, count: number }>();
            playtimeData.forEach(item => {
                const current = statsMap.get(item.player_id) || { total: 0, count: 0 };
                statsMap.set(item.player_id, {
                    total: current.total + (item.steam_playtime || 0),
                    count: current.count + 1
                });
            });

            rankedPlayers = profiles.map(p => {
                const stats = statsMap.get(p.id) || { total: 0, count: 0 };
                const value = type === 'playtime' ? stats.total : stats.count;
                const shopItem: any = p.shop_items;
                const borderStyle = shopItem?.metadata?.style || shopItem?.metadata?.image_url;

                return {
                    id: p.id,
                    display_name: p.display_name,
                    avatar_url: p.avatar_url,
                    value: value,
                    formattedValue: type === 'playtime' ? `${Math.round(value / 60)}h` : `${value} games`,
                    borderStyle: borderStyle
                };
            });
        } else if (type === 'missions') {
            const { data: missionsData, error: missionsError } = await supabase
                .from('player_missions')
                .select('player_id');

            if (missionsError) throw missionsError;

            const missionsMap = new Map<string, number>();
            missionsData.forEach(item => {
                missionsMap.set(item.player_id, (missionsMap.get(item.player_id) || 0) + 1);
            });

            rankedPlayers = profiles.map(p => {
                const count = missionsMap.get(p.id) || 0;
                const shopItem: any = p.shop_items;
                const borderStyle = shopItem?.metadata?.style || shopItem?.metadata?.image_url;

                return {
                    id: p.id,
                    display_name: p.display_name,
                    avatar_url: p.avatar_url,
                    value: count,
                    formattedValue: `${count} missions`,
                    borderStyle: borderStyle
                };
            });
        }

        // Sort by value descending
        const allPlayers = rankedPlayers
            .sort((a, b) => b.value - a.value)
            .filter(p => type === 'experience' ? true : p.value > 0);

        const total = allPlayers.length;
        const start = (page - 1) * limit;
        const paginatedPlayers = allPlayers.slice(start, start + limit);

        return {
            players: paginatedPlayers,
            total: total
        };

    } catch (error) {
        console.error('Error fetching player rankings:', error);
        return { players: [], total: 0 };
    }
}
